import { type DateValue, getLocalTimeZone } from '@internationalized/date';

import { FilterFormLabels, FilterFormValues } from './types';
import { OptionList, FilteredValues } from '$saga/types/search.types';
import { defaultFormLabels, defaultFormValues } from './constants';

/**
 * Format selected value ids to their corresponding label
 * @param currentValue
 * @param options
 */
export const findCurrentSelectedFilterLabel = (
    currentValue: string,
    options: FilteredValues | OptionList,
) => {
    if (currentValue === '') return '';

    return (
        options.find((currentOption) => currentOption.value === currentValue)
            ?.label ?? ''
    );
};

/**
 * Initialize form with selected values
 * @param values
 * @param defaultDestinationOptions
 * @param defaultSportOptions
 *
 * @param defaultLevels
 * @param defaultTypes
 * @param defaultTags
 * @param defaultDurationMin
 * @param defaultDurationMax
 * @param defaultBudgetMin
 * @param defaultBudgetMax
 */
export const initializeLabelValues = (
    values: FilterFormValues,
    defaultDestinationOptions: OptionList,
    defaultSportOptions: OptionList,
    defaultLevels: OptionList,
    defaultTypes: OptionList,
    defaultTags: OptionList,
    defaultDurationMin: number,
    defaultDurationMax: number,
    defaultBudgetMin: number,
    defaultBudgetMax: number,
): FilterFormLabels => {
    const destinationLabels = values.destination.map((dest) =>
        dest === 'all'
            ? 'Toutes les destinations'
            : findCurrentSelectedFilterLabel(dest, defaultDestinationOptions),
    );
    const sportLabels = values.theme.map((theme) =>
        theme === 'all'
            ? 'Tous les sports'
            : findCurrentSelectedFilterLabel(theme, defaultSportOptions),
    );

    // Secondary filters
    const levelsLabel = values.levels
        .map((val) => findCurrentSelectedFilterLabel(val, defaultLevels))
        .join(', ');

    const tagsLabel = values.tags
        .map((val) => findCurrentSelectedFilterLabel(val, defaultTags))
        .join(', ');

    const typesLabel = values.types
        .map((val) => findCurrentSelectedFilterLabel(val, defaultTypes))
        .join(', ');

    return {
        destination: destinationLabels,
        theme: sportLabels,
        startDate: values.startDate,
        endDate: values.endDate,
        nbPax: values.nbPax,
        // secondary
        levels: levelsLabel,
        types: typesLabel,
        duration: `${String(values.durationMin ?? defaultDurationMin)}ms -
            ${String(values.durationMax ?? defaultDurationMax)}ms`,
        budget: `${String(
            values.budgetMin ?? defaultBudgetMin,
        )} - ${String(values.budgetMax ?? defaultBudgetMax)}`,
        tags: tagsLabel,
    };
};

/**
 * Reset form with initial default values
 */
export const resetLabels = () => defaultFormLabels;

/**
 * Reset form with initial default labels
 */
export const resetValues = () => defaultFormValues;

export const isDateSelectionFullMonth = (
    startDate: DateValue | undefined,
    endDate: DateValue | undefined,
) => {
    if (!startDate || !endDate) return false;

    const start = startDate.toDate(getLocalTimeZone());
    const end = endDate.toDate(getLocalTimeZone());
    const isStartFirstOfMonth = start.getDate() === 1;
    const isEndLastOfMonth =
        end.getDate() ===
        new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();

    return isStartFirstOfMonth && isEndLastOfMonth;
};
